import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Eye, EyeOff, GripVertical } from 'lucide-react';
import api, { getApiUrl } from '../../utils/api';

const API_URL = getApiUrl();

const ProjectsManagement = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchProjects = useCallback(async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const toggleActive = async (project) => {
    try {
      await api.put(`/projects/${project.id}`, { active: !project.active });
      fetchProjects();
    } catch (error) {
      console.error('Error updating project:', error);
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm('Bu projeyi silmek istediğinize emin misiniz?')) return;
    try {
      await api.delete(`/projects/${id}`);
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  return (
    <div data-testid="admin-projects">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Projeler</h1>
          <p className="text-gray-400">Projeleri yönetin</p>
        </div>
        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <Link
            to="/admin/projects/new"
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl font-semibold 
                     flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Yeni Proje
          </Link>
        </motion.div>
      </div>

      {loading ? (
        <div className="text-gray-400">Yükleniyor...</div>
      ) : projects.length === 0 ? (
        <div className="text-center py-12 glass rounded-2xl">
          <p className="text-gray-400 mb-4">Henüz proje eklenmemiş</p>
          <Link
            to="/admin/projects/new"
            className="text-blue-400 hover:text-blue-300"
          >
            İlk projeyi ekle
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="flex items-center gap-4 p-4 rounded-xl glass"
            >
              <GripVertical className="w-5 h-5 text-gray-500 cursor-grab" />
              
              {/* Thumbnail */}
              {project.image && (
                <div className="w-16 h-12 rounded-lg overflow-hidden bg-gray-800">
                  <img 
                    src={project.image.startsWith('http') ? project.image : `${API_URL}${project.image}`}
                    alt={project.title_tr}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              {/* Info */}
              <div className="flex-1">
                <h3 className="font-semibold">{project.title_tr}</h3>
                <p className="text-sm text-gray-400">{project.short_desc_tr?.slice(0, 60)}...</p>
              </div>
              
              {/* Status */}
              <span className={`px-3 py-1 rounded-full text-xs ${
                project.active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
              }`}>
                {project.active ? 'Aktif' : 'Pasif'}
              </span>
              
              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleActive(project)}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  title={project.active ? 'Pasif yap' : 'Aktif yap'}
                >
                  {project.active ? (
                    <Eye className="w-5 h-5 text-green-400" />
                  ) : (
                    <EyeOff className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                <button
                  onClick={() => navigate(`/admin/projects/${project.id}`)}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  title="Düzenle"
                >
                  <Edit className="w-5 h-5 text-blue-400" />
                </button>
                <button
                  onClick={() => deleteProject(project.id)}
                  className="p-2 rounded-lg hover:bg-red-500/20 transition-colors"
                  title="Sil"
                >
                  <Trash2 className="w-5 h-5 text-red-400" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectsManagement;
